import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOSchema from '../components/SEOSchema';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProgramFramework from '../components/ProgramFramework';
import ReviewSnippets from '../components/ReviewSnippets';
import { 
  Stethoscope, 
  Apple, 
  CheckCircle, 
  ArrowRight, 
  Heart,
  Activity,
  Shield,
  Target,
  ChevronDown,
  Pill,
  Droplet,
  Scale
} from 'lucide-react';

const MedicalNutritionTherapyPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const conditions = [
    {
      icon: <Droplet className="w-6 h-6" />,
      title: "Diabetes Management",
      description: "Evidence-based nutrition strategies for Type 1, Type 2, and pre-diabetes. Blood sugar optimization through dietary modifications and meal timing.",
      color: "blue"
    },
    {
      icon: <Activity className="w-6 h-6" />,
      title: "Thyroid Disorders",
      description: "Nutritional support for hypothyroidism, hyperthyroidism, and Hashimoto's. Focus on iodine, selenium, and metabolism-supporting nutrients.",
      color: "emerald"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "PMOS (previously called PCOS)",
      description: "Hormone-balancing nutrition to manage polycystic ovary syndrome symptoms, improve insulin sensitivity, and support regular cycles.",
      color: "pink"
    },
    {
      icon: <Scale className="w-6 h-6" />,
      title: "Obesity and Metabolic Syndrome",
      description: "Medically supervised weight management addressing underlying metabolic dysfunction, not just calorie restriction.",
      color: "orange"
    },
    {
      icon: <Pill className="w-6 h-6" />,
      title: "Hypertension",
      description: "DASH-inspired dietary approaches to reduce blood pressure naturally through strategic sodium management and potassium-rich foods.",
      color: "cyan"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Digestive Health",
      description: "Nutrition protocols for IBS, acid reflux, and other gastrointestinal conditions. Gut health optimization through targeted dietary changes.",
      color: "violet"
    }
  ];

  const whatMakesUsDifferent = [
    {
      title: "Doctor-Designed Plans",
      description: "Every nutrition plan is created by our MBBS-qualified medical lead with specialized training in nutrition and internal medicine."
    },
    {
      title: "Medication Consideration",
      description: "We account for drug-nutrient interactions and design meals that complement your medications, not conflict with them."
    },
    {
      title: "Lab Report Integration",
      description: "Your blood work and health markers guide our recommendations. We monitor changes and adjust plans based on clinical outcomes."
    },
    {
      title: "Multi-Specialist Team",
      description: "Access gynecologists for hormonal conditions, fertility specialists for reproductive health, and certified nutritionists for daily guidance."
    },
    {
      title: "Continuous Medical Oversight",
      description: "Regular check-ins with medical professionals ensure your personalized nutrition plan remains aligned with your health progression."
    },
    {
      title: "Evidence-Based Approach",
      description: "All recommendations are grounded in clinical research and medical guidelines, not trends or fads."
    }
  ];

  const mntProcess = [
    {
      step: 1,
      title: "Medical History Review",
      description: "Comprehensive assessment of your health conditions, medications, family history, and lifestyle factors."
    },
    {
      step: 2,
      title: "Lab Analysis",
      description: "Review of your blood work, metabolic markers, and relevant diagnostic tests to understand your baseline."
    },
    {
      step: 3,
      title: "Personalized Nutrition Plan Design",
      description: "Creation of a nutrition plan specifically targeting your medical conditions with appropriate modifications."
    },
    {
      step: 4,
      title: "Implementation Support",
      description: "Practical meal planning, grocery guidance, and daily support to make your personalized nutrition plan achievable."
    },
    {
      step: 5,
      title: "Monitoring and Adjustment",
      description: "Regular review of health markers and symptoms, with plan modifications based on your response."
    }
  ];

  const faqs = [
    {
      question: "What is Clinical Nutrition?",
      answer: "Clinical Nutrition is an evidence-based approach to using specific dietary interventions to support the management of health conditions. Unlike general nutrition advice, it is personalized based on your individual diagnosis, medications, and health goals. It involves thorough medical assessment, individualized nutrition planning, and ongoing monitoring by healthcare professionals. At TCUBE FITNESS™, our Clinical Nutrition programs are designed by doctors and implemented by qualified nutritionists."
    },
    {
      question: "Who needs Clinical Nutrition?",
      answer: "MNT is beneficial for anyone managing chronic conditions such as diabetes, heart disease, kidney disease, thyroid disorders, PMOS, obesity, or digestive issues. It is also valuable for those on multiple medications, individuals preparing for or recovering from surgery, and anyone whose health condition requires more than basic nutrition guidance. If your doctor has recommended dietary changes for a health condition, MNT provides the structured support to implement those changes effectively."
    },
    {
      question: "How is MNT different from regular diet plans?",
      answer: "Regular diet plans focus primarily on weight management or general health. Clinical Nutrition specifically targets disease management and prevention through personalized planning. It considers your complete medical picture, including diagnoses, medications, lab values, and symptoms. The plans are designed by or in consultation with physicians, not just fitness enthusiasts. Results are measured not just by weight but by clinical outcomes like blood sugar levels, lipid profiles, and symptom improvement."
    },
    {
      question: "Can Clinical Nutrition replace medications?",
      answer: "Clinical Nutrition is designed to complement, not replace, your medical treatment. In some cases, effective personalized nutrition can reduce medication requirements, but this should only happen under your doctor's supervision. Our approach is to work alongside your healthcare team, supporting your medical treatment with optimal nutrition. Many clients do see improved health markers that allow their physicians to adjust medications, but this is always a medical decision."
    },
    {
      question: "What conditions do you support with Clinical Nutrition?",
      answer: "Our MNT programs address diabetes and pre-diabetes, thyroid disorders (hypothyroidism, hyperthyroidism, Hashimoto's), PMOS and hormonal imbalances, obesity and metabolic syndrome, hypertension and cardiovascular health, digestive disorders, pre and post-operative nutrition, and general metabolic health optimization. Each program is customized to your specific condition and severity."
    },
    {
      question: "How long does Clinical Nutrition take to show results?",
      answer: "Results vary based on the condition being supported and individual factors. Some clients notice symptom improvements within 2-4 weeks, while measurable changes in blood markers typically appear within 6-12 weeks. Chronic condition management is an ongoing process, which is why we recommend minimum 12-week programs for meaningful change and 25-week programs for sustainable long-term improvement. Our team monitors your progress throughout and adjusts the plan as needed."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Doctor-Led Diabetes &amp; Clinical Nutrition Program | TCUBE FITNESS™</title>
        <meta name="description" content="Doctor-led diabetes nutrition and clinical nutrition for prediabetes, insulin resistance, thyroid, PMOS, obesity and metabolic health. Personalized, evidence-based and 100% online, to support blood sugar and weight management alongside your treating doctor." />
        <meta name="keywords" content="doctor led diabetes nutrition, diabetes diet, diabetes diet plan, Indian diet for diabetes, diabetes nutritionist, prediabetes diet, insulin resistance diet, diabetes weight management, clinical nutrition, medical nutrition therapy, diabetes diet for parents, HbA1c, blood glucose management" />
        <link rel="canonical" href="https://tcubefitness.com/medical-nutrition-therapy" />
        <meta property="og:title" content="Clinical Nutrition | Doctor-Led Personalized Nutrition Programs | TCUBE FITNESS™" />
        <meta property="og:description" content="Evidence-based clinical nutrition for chronic conditions. Doctor-designed, medically supervised personalized nutrition plans." />
        <meta property="og:url" content="https://tcubefitness.com/medical-nutrition-therapy" />
        <meta property="og:type" content="website" />
      </Helmet>

      <SEOSchema
        type="service"
        name="Clinical Nutrition Program"
        description="Evidence-based clinical nutrition for diabetes, thyroid, PMOS, obesity and metabolic wellness. It is doctor-designed, personalized and medically supervised."
        url="/medical-nutrition-therapy"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Clinical Nutrition', path: '/medical-nutrition-therapy' },
        ]}
      />

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-28 pb-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-600/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-6">
                <Stethoscope className="w-4 h-4" />
                Doctor-Led Clinical Nutrition
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Doctor-Led Diabetes &amp; Clinical Nutrition
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Personalized, evidence-based nutrition led by a General Medicine (Internal Medicine) physician, to support blood sugar and weight management for diabetes, prediabetes and insulin resistance, as well as thyroid, PMOS, obesity and metabolic health. Designed to complement your medical treatment, with ongoing supervision. Also suitable if you are seeking a practical diabetes diet for your parents.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <Link
                  to="/book-consultation?from=clinical-nutrition-page"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-emerald-500/25"
                  data-testid="mnt-cta"
                >
                  Start Your Clinical Nutrition Program
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="https://wa.me/919515901777"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-slate-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-800"
                >
                  Free Consultation
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-emerald-400 font-bold text-lg">MBBS</p>
                  <p className="text-slate-500 text-sm">Doctor Designed</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-blue-400 font-bold text-lg">Evidence</p>
                  <p className="text-slate-500 text-sm">Based Approach</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-orange-400 font-bold text-lg">Lab</p>
                  <p className="text-slate-500 text-sm">Monitored</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-pink-400 font-bold text-lg">Chronic</p>
                  <p className="text-slate-500 text-sm">Care Specialists</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 text-center">
                The Science of <span className="text-emerald-400">Clinical Nutrition</span>
              </h2>
              
              <div className="bg-slate-900/50 rounded-3xl p-8 border border-slate-800 mb-12">
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  Clinical Nutrition is not a diet. It is an evidence-based intervention. Recognized by medical institutions worldwide, it uses food as a strategic tool to prevent and manage chronic conditions. The right nutrients in the right amounts can influence blood sugar, hormones, inflammation, and metabolism at a cellular level.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  At TCUBE FITNESS™, our Clinical Nutrition programs are designed by <Link to="/#team" className="text-emerald-400 hover:underline">Dr. Pooja Sree (MBBS, DNB General Medicine)</Link>, who combines medical training with specialized nutrition expertise. Unlike generic diet advice, every recommendation is based on your diagnosis, lab values, medications, and individual response.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed">
                  Whether you are newly diagnosed with a chronic condition or have been managing one for years, our personalized clinical nutrition approach can help optimize your health outcomes, always in coordination with your healthcare team.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Conditions Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Conditions We <span className="text-emerald-400">Treat</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Specialized nutrition protocols for chronic health conditions
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {conditions.map((condition, idx) => (
                <div 
                  key={idx}
                  className={`bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-${condition.color}-500/30 transition-all`}
                >
                  <div className={`w-12 h-12 bg-${condition.color}-500/10 rounded-xl flex items-center justify-center text-${condition.color}-400 mb-4`}>
                    {condition.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{condition.title}</h3>
                  <p className="text-slate-400 leading-relaxed text-sm">{condition.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Us Different Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                What Makes Our MNT <span className="text-blue-400">Different</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                A clinical approach to nutrition that goes beyond basic diet advice
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {whatMakesUsDifferent.map((item, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800"
                >
                  <h3 className="text-lg font-semibold text-white mb-3">{item.title}</h3>
                  <p className="text-slate-400 leading-relaxed text-sm">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Our MNT <span className="text-emerald-400">Process</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                A systematic, clinically-informed approach to personalized nutrition
              </p>
            </div>

            <div className="max-w-3xl mx-auto">
              <div className="relative">
                <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-emerald-600 via-teal-600 to-emerald-600 hidden md:block"></div>
                
                <div className="space-y-6">
                  {mntProcess.map((item, idx) => (
                    <div key={idx} className="flex gap-6 items-start">
                      <div className="relative z-10 w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 shadow-lg">
                        {item.step}
                      </div>
                      <div className="bg-slate-900/50 rounded-2xl p-5 border border-slate-800 flex-1">
                        <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                        <p className="text-slate-400">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pre/Post Op Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-blue-900/30 via-cyan-900/30 to-blue-900/30 rounded-3xl p-8 border border-blue-500/20">
                <div className="text-center mb-8">
                  <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                    Pre-Operative and Post-Operative Nutrition
                  </h2>
                  <p className="text-slate-300">
                    Specialized MNT protocols to prepare for surgery and optimize recovery
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800">
                    <h3 className="text-lg font-semibold text-white mb-3">Pre-Operative Nutrition</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        Build nutritional reserves before surgery
                      </li>
                      <li className="flex items-start gap-2 text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        Optimize blood sugar and metabolic markers
                      </li>
                      <li className="flex items-start gap-2 text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                        Reduce surgical complications risk
                      </li>
                    </ul>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800">
                    <h3 className="text-lg font-semibold text-white mb-3">Post-Operative Nutrition</h3>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2 text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Accelerate wound healing
                      </li>
                      <li className="flex items-start gap-2 text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Prevent muscle loss during recovery
                      </li>
                      <li className="flex items-start gap-2 text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                        Rebuild strength safely
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Clinical Nutrition <span className="text-emerald-400">FAQs</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Common questions about our personalized clinical nutrition programs
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180 text-emerald-400' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all ${openFaq === idx ? 'max-h-[500px]' : 'max-h-0'}`}>
                    <p className="px-5 pb-5 text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/50 via-teal-900/50 to-emerald-900/50"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Take Control of Your Health with MNT
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Start your evidence-based clinical nutrition journey with a free medical consultation.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-100"
                >
                  Book Your Consultation
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/packages"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-white/10"
                >
                  View Programs
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 border-t border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Related Programs</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/doctor-led-fitness" className="text-slate-400 hover:text-blue-400 transition-colors">Doctor-Led Fitness</Link>
                <span className="text-slate-700">|</span>
                <Link to="/pregnancy-nutrition" className="text-slate-400 hover:text-emerald-400 transition-colors">Pregnancy Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/fertility-nutrition" className="text-slate-400 hover:text-pink-400 transition-colors">Fertility Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/online-fitness-coaching" className="text-slate-400 hover:text-orange-400 transition-colors">Online Fitness Coaching</Link>
                <span className="text-slate-700">|</span>
                <Link to="/packages" className="text-slate-400 hover:text-cyan-400 transition-colors">All Programs</Link>
              </div>
            </div>
          </div>
        </section>

        <ReviewSnippets
          ids={["sudarshan-diabetes-gut", "aruna-diabetes"]}
          heading="Real diabetes & metabolic health journeys"
          subheading="Genuine experiences from people we have supported. Nutrition complements medical care; individual results vary."
        />

        {/* Diabetes hub link */}
        <section className="pb-16 relative">
          <div className="container mx-auto px-4 sm:px-6 text-center">
            <Link to="/hub/diabetes-metabolic-health" className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-semibold text-sm sm:text-base" data-testid="mnt-hub-link">
              Explore the full Diabetes &amp; Metabolic Health hub
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </section>

        <ProgramFramework slug="medical-nutrition-therapy" />
        <Footer />
      </div>
    </>
  );
};

export default MedicalNutritionTherapyPage;
